import React, { useRef } from "react";
//#region types
import { ChartEnums } from "models/enums";
import { ChartContainerProps, ChartPeriodClickArgs } from "models/propTypes/chartProps";
//#endregion
//#region components
import useFilterContext from "../filter/context/useFilterContext";
import useChartContext from "./context/useContext";
import Chart from "./chart";
//#endregion

const ChartContainerInner: React.FC<ChartContainerProps> = (props) => {
  const {
    chartAPIKey,
    accumulatedChartAPIKey,
    usePageFilter,
    filter: staticFilter = {},
    filterToAPIHandler,
    ...otherProps
  } = props;

  const [currentChartData, setCurrentChartData] = React.useState<{ chartKey: string; filter: object }>();
  const [frozenChartData, setFrozenChartData] = React.useState<{ chartKey: string; filter: object } | null>(null);
  const [isSelectionFrozen, setIsSelectionFrozen] = React.useState<boolean>(false);
  const [anyChartFrozen, setAnyChartFrozen] = React.useState(false);
  const [justFrozen, setJustFrozen] = React.useState(false);

  const { filter, setFilter, queryHasFilterProps } = useFilterContext<any>();
  const chartContext = useChartContext();

  type FilterRefType = { filter: typeof filter; setFilter: typeof setFilter };
  const filterRef = useRef<FilterRefType | null>(null);
  filterRef.current = { filter, setFilter };

  React.useEffect(() => {
    setAnyChartFrozen(!!chartContext?.selectedChartId);
  }, [chartContext?.selectedChartId]);

  React.useEffect(() => {
    if (isSelectionFrozen && frozenChartData) {
      setCurrentChartData(frozenChartData);
      return;
    }

    const isRespondingToChartClick = anyChartFrozen && !isSelectionFrozen;

    const effectiveFilter =
      (usePageFilter || isRespondingToChartClick) && filter
        ? filterToAPIHandler
          ? filterToAPIHandler(filter)
          : filter
        : staticFilter;

    setCurrentChartData({
      chartKey:
        chartContext && chartContext.type === ChartEnums.DisplayType.Accumulated && accumulatedChartAPIKey
          ? accumulatedChartAPIKey
          : chartAPIKey,
      filter: effectiveFilter,
    });
  }, [filter, chartContext?.type, chartAPIKey, isSelectionFrozen, frozenChartData, anyChartFrozen]);

  React.useEffect(() => {
    const resolvedChartId = `${chartAPIKey}${`_${(props as any)?.id ?? ""}`}`;
    const isThisSelected = chartContext?.selectedChartId === resolvedChartId;
    setIsSelectionFrozen(!!isThisSelected);
    if (!isThisSelected) {
      setFrozenChartData(null);
      setJustFrozen(false);
    }
  }, [chartAPIKey, (props as any)?.id, chartContext?.selectedChartId]);

  React.useEffect(() => {
    if (justFrozen && filter && typeof filter === "object" && Object.keys(filter).length > 0) {
      setJustFrozen(false);
    }
  }, [filter, justFrozen]);

  React.useEffect(() => {
    const hasQueryFilters = typeof queryHasFilterProps === "function" ? queryHasFilterProps() : false;
    const isEffectivelyEmpty = !filter || (typeof filter === "object" && Object.keys(filter).length === 0);

    if (justFrozen) {
      return;
    }

    const shouldAutoClear = usePageFilter
      ? isSelectionFrozen && (isEffectivelyEmpty || !hasQueryFilters)
      : isSelectionFrozen && isEffectivelyEmpty;

    if (shouldAutoClear) {
      try {
        setIsSelectionFrozen(false);
        setFrozenChartData(null);
        chartContext?.setSelectedChartId && chartContext.setSelectedChartId(null);
      } catch {}
    }
  }, [filter, isSelectionFrozen, chartAPIKey, (props as any)?.id, queryHasFilterProps, usePageFilter, justFrozen]);

  const handleOnDataPointClicked = (index: number, label?: any) => {
    if (props.onDataPointClickedSetFilter) {
      setFrozenChartData(currentChartData!);
      setIsSelectionFrozen(true);
      setJustFrozen(true);
      const payload = props.onDataPointClickedSetFilter(label) ?? {};
      const current = (filterRef.current?.filter as any) || {};
      const merged = { ...current, ...payload };
      if (anyChartFrozen) {
        filterRef.current?.setFilter(payload);
      } else {
        filterRef.current?.setFilter(merged);
      }
      try {
        const resolvedChartId = `${chartAPIKey}${`_${(props as any)?.id ?? ""}`}`;
        chartContext?.setSelectedChartId && chartContext.setSelectedChartId(resolvedChartId);
      } catch {}
      return;
    }
    props.onDataPointClick && props.onDataPointClick(index, label);
  };

  const handleOnBackgroundClicked = () => {
    if (props.onBackgroundClickedSetFilter) {
      setIsSelectionFrozen(false);
      setFrozenChartData(null);
      setJustFrozen(false);
      filterRef.current?.setFilter({});
      chartContext?.setSelectedChartId && chartContext.setSelectedChartId(null);
      return;
    }
    chartContext?.setSelectedChartId && chartContext.setSelectedChartId(null);
    props.onBackgroundClick && props.onBackgroundClick();
  };

  const handleOnPeriodClick = (args: ChartPeriodClickArgs) => {
    if (props.onDataPointClickedSetFilter) {
      setFrozenChartData(currentChartData!);
      setIsSelectionFrozen(true);
      setJustFrozen(true);
      const payload = props.onDataPointClickedSetFilter(args) ?? {};
      const current = (filterRef.current?.filter as any) || {};
      const merged = { ...current, ...payload };
      if (anyChartFrozen) {
        filterRef.current?.setFilter(payload);
      } else {
        filterRef.current?.setFilter(merged);
      }
      try {
        const resolvedChartId = `${chartAPIKey}${`_${(props as any)?.id ?? ""}`}`;
        chartContext?.setSelectedChartId && chartContext.setSelectedChartId(resolvedChartId);
      } catch {}
      return;
    }
    props.onPeriodClick && props.onPeriodClick(args);
  };

  return currentChartData ? (
    <Chart
      {...otherProps}
      chartAPIKey={currentChartData.chartKey}
      filter={currentChartData.filter}
      onDataPointClick={handleOnDataPointClicked}
      onBackgroundClick={handleOnBackgroundClicked}
      onPeriodClick={handleOnPeriodClick}
    />
  ) : (
    <></>
  );
};

export default ChartContainerInner;
